import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { SchoolApi } from '../../school_panel/api/client'
import { useSectionsList } from '../../school_panel/api/hooks'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { useExamQuestionGet } from '../../react-query/queries'
import { Chip, KpiCard, Spinner, PagedFooter, Table, Td, Th, Track } from '../../school_panel/components/ui'
import { usePagination } from '../../school_panel/lib/usePagination'
import { audienceText, dueText, releaseText, type AssignValue } from './AssignFields'
import { audienceSummary, chapterCoverage, isFailedResponse, reviewStats, toReviewQuestions, type ReviewQuestion } from './examReview'

const FIX_LINK = 'inline-flex min-h-[34px] flex-none items-center rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink no-underline transition-colors hover:border-slate hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'

const Check = ({ ok, title, sub, fix, fixLabel = 'Fix' }: { ok: boolean; title: string; sub: string; fix?: string; fixLabel?: string }) => (
  <div className="flex items-start gap-3 border-b border-hairline py-3 last:border-b-0">
    <span aria-hidden className={`grid h-7 w-7 flex-none place-items-center rounded-[9px] text-sm font-bold ${ok ? 'bg-success-bg text-success' : 'bg-warning-bg text-warning-ink'}`}>{ok ? '✓' : '!'}</span>
    <span className="min-w-0 flex-1">
      <span className="block font-semibold text-ink">{title}</span>
      <span className="text-[13px] text-muted">{sub}</span>
    </span>
    {fix && <Link to={fix} className={FIX_LINK} aria-label={`${fixLabel}: ${title}`}>{fixLabel}</Link>}
  </div>
)

const shorten = (s: string, n = 28) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

/**
 * Design ExamEditor step 3, "Review & publish": stat tiles, the "Before you publish"
 * checks, chapter coverage and an answer-key table, all computed from the saved questions.
 * The footer (Save as draft / Publish exam) lives in the wizard.
 */
export default function ExamReviewStep({ examId, questions, timeMinutes, assign, gradeId, gradeLabel, partial }: {
  examId: string
  questions: ReviewQuestion[]
  timeMinutes: number | null
  assign: AssignValue
  gradeId: number | null
  gradeLabel: string
  /** Set when the server stored fewer questions than were sent (it lists problems, not which ones). */
  partial?: { saved: number; sent: number; errors: string[] } | null
}) {
  const sectionsQuery = useSectionsList(gradeId)
  const sections = sectionsQuery.data?.sections ?? []
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades(), retry: false })
  const gradeStudents = (gradesQ.data?.grades ?? []).find((g) => g.id === gradeId)?.student_count ?? null
  const aud = audienceSummary({ mode: assign.mode, gradeStudentCount: gradeStudents, sections, sectionIds: assign.sectionIds, studentIds: assign.studentIds })

  // After a partial save the sent list is not what exists: read the exam's own questions (it is brand new, so that is the saved set).
  const { token } = useAuth()
  const serverQ = useExamQuestionGet(token, partial ? examId : undefined)
  const serverData: any = partial ? serverQ.data : null
  const serverFull: any[] | null = serverData && !isFailedResponse(serverData) && Array.isArray(serverData.full) ? serverData.full : null
  const rows = useMemo(() => serverFull
    ? toReviewQuestions(
        serverFull.map((q) => ({ ques: q.ques, choice_a: q.option1, choice_b: q.option2, choice_c: q.option3, choice_d: q.option4, ans: q.correct_ans, explanation: q.explanation })),
        serverFull.map((q) => q.chapter_name))
    : questions, [serverFull, questions])
  const waiting = !!partial && !serverFull
  const loadFailed = waiting && (serverQ.isError || (serverData != null && isFailedResponse(serverData)))

  const stats = reviewStats(waiting ? [] : rows, timeMinutes)
  const savedCount = stats.total
  const coverage = chapterCoverage(waiting ? [] : rows)
  const pg = usePagination(waiting ? [] : rows, 10)
  const detail = `/exams/exam-detail/${examId}`
  const who = audienceText(assign, sections, gradeLabel)
  const release = releaseText(assign.resultsRelease)
  const noKeys = stats.total - stats.keysSet

  return (
    <div className="min-w-0">
      {partial && (
        <div role="alert" className="mb-[18px] rounded-[14px] border border-warning/40 bg-warning-bg px-4 py-3 text-sm text-warning-ink">
          <p className="font-semibold">{partial.sent - partial.saved} of {partial.sent} questions could not be saved.</p>
          {partial.errors.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {partial.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {partial.errors.length > 5 && <li>and {partial.errors.length - 5} more</li>}
            </ul>
          )}
          <p className="mt-1">The figures below are read from the questions that were saved. Open the exam to check which questions are missing before you publish.</p>
        </div>
      )}
      {waiting && (
        loadFailed
          ? <p role="alert" className="rounded-[14px] border border-line bg-surface px-4 py-3 text-sm text-muted">The saved questions could not be loaded. <Link to={detail} className="font-semibold text-ink underline">Open the exam</Link> to check them before you publish.</p>
          : <div className="flex items-center gap-2 rounded-[14px] border border-line bg-surface px-4 py-3 text-sm text-muted"><Spinner size="sm" /> Loading the saved questions…</div>
      )}
      {!waiting && <>
      <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-3">
        <KpiCard label="Questions" size="lg" value={savedCount}
          sub={coverage.length ? `${coverage.length} chapter${coverage.length === 1 ? '' : 's'} covered` : 'No chapters assigned'} />
        <KpiCard label="Time given" size="lg" value={timeMinutes ?? '—'} unit={timeMinutes ? ' min' : undefined}
          sub={stats.minutesPerQuestion ? `about ${stats.minutesPerQuestion} min per question` : undefined} />
        <KpiCard label="Audience" size="lg" value={aud.count ?? '—'} sub={aud.caption} />
      </div>

      <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-12">
        <section aria-labelledby="ck-title" className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card lg:col-span-5">
          <h2 id="ck-title" className="mb-[18px] font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Before you publish</h2>
          <Check ok={noKeys === 0} title={noKeys === 0 ? 'Every question has a correct answer' : `${noKeys} question${noKeys === 1 ? ' has' : 's have'} no correct answer`}
            sub={`${Math.min(stats.keysSet, savedCount)} of ${savedCount} answer keys set`} fix={noKeys === 0 ? undefined : detail} />
          {stats.withoutExplanation > 0
            ? <Check ok={false} title={`${stats.withoutExplanation} question${stats.withoutExplanation === 1 ? ' has' : 's have'} no explanation`} sub="Optional, but students learn more from them" fix={detail} />
            : <Check ok title="Every question has an explanation" sub="Shown when results are released" />}
          <Check ok title={`Assigned to ${who}`} sub={dueText(assign).replace(/^due /, 'Due ').replace(/^no due date$/, 'No due date')} fix={detail} />
          <Check ok title={`Results shown ${release}`} sub="You can change this after publishing" />

          {coverage.length > 0 && (
            <>
              <p className="mb-2.5 mt-[22px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Chapter coverage</p>
              {coverage.map((c) => (
                <div key={c.name} className="mb-3">
                  <div className="mb-1.5 flex justify-between gap-3 text-[13px]">
                    <span className="min-w-0 font-medium text-ink">{c.name}</span>
                    <span className="flex-none font-mono font-medium text-ink">{c.count} question{c.count === 1 ? '' : 's'}</span>
                  </div>
                  <Track value={c.pct} label={`${c.name} coverage`} />
                </div>
              ))}
            </>
          )}
        </section>

        <section aria-labelledby="pv-title" className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card lg:col-span-7">
          <div className="mb-[18px] flex items-center justify-between gap-3">
            <h2 id="pv-title" className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Answer key preview</h2>
            <Link to={detail} className="inline-flex min-h-[32px] items-center gap-1 text-[13px] font-semibold text-muted-2 no-underline hover:text-ink hover:underline hover:underline-offset-[3px]">Edit questions →</Link>
          </div>
          <Table minWidth={520}>
            <thead><tr><Th>#</Th><Th>Question</Th><Th>Chapter</Th><Th align="right">Answer</Th></tr></thead>
            <tbody>
              {pg.pageItems.map((q, k) => {
                const n = pg.from + k
                return (
                  <tr key={n}>
                    <Td className="font-mono text-muted">{n}</Td>
                    <Td className="max-w-[340px]"><span className="line-clamp-2">{q.stem}</span></Td>
                    <Td className="whitespace-nowrap text-[13px] text-muted">{q.chapter ?? '—'}</Td>
                    <Td align="right">
                      {q.answerLetter || q.answerText
                        ? <Chip tone="up">{[q.answerLetter, q.answerText && shorten(q.answerText)].filter(Boolean).join(' · ')}</Chip>
                        : <span className="text-muted">—</span>}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          <PagedFooter divider="list" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Answer key pagination" />
        </section>
      </div>
      </>}
    </div>
  )
}
